import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import React__default, { forwardRef, useRef, useEffect, useMemo, useCallback } from 'react';
import OutlinedInput from '@mui/material/OutlinedInput';
import useForkRef from '@mui/utils/useForkRef';
import useCharacterLimit from './hooks/useCharacterLimit/index.js';
import { getInputTypographyVariant, getInputClasses } from './utils.js';

var _excluded = ["appearance", "characterLimit", "color", "endAdornment", "inputProps", "inputRef", "onChange", "onChangeText", "shape", "size", "startAdornment", "value", "variant"];
/**
 * autofocus prop has been changed from mount only to render as prop changes
 */
var Input = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$appearance = props.appearance,
    appearance = _props$appearance === void 0 ? 'outline' : _props$appearance,
    characterLimit = props.characterLimit,
    _props$color = props.color,
    color = _props$color === void 0 ? 'text-text-primary' : _props$color,
    endAdornmentProp = props.endAdornment,
    inputPropsProp = props.inputProps,
    inputRef = props.inputRef,
    onChangeProp = props.onChange,
    onChangeText = props.onChangeText,
    _props$shape = props.shape,
    shape = _props$shape === void 0 ? 'square' : _props$shape,
    _props$size = props.size,
    size = _props$size === void 0 ? 'medium' : _props$size,
    startAdornment = props.startAdornment,
    value = props.value,
    variant = props.variant,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  var internalInputRef = useRef(null);
  var forkedInputRef = useForkRef(internalInputRef, inputRef);
  useEffect(function () {
    if (props.autoFocus && internalInputRef.current) {
      internalInputRef.current.focus();
      //developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/setSelectionRange
      var validInputTypes = ['text', 'search', 'url', 'tel', 'password'];
      // focusing on the end of the input
      // validation of input type is one of the above or it is a textarea
      if (validInputTypes.includes(internalInputRef.current.type) || internalInputRef.current.tagName.toLowerCase() === 'textarea') {
        internalInputRef.current.setSelectionRange(internalInputRef.current.value.length, internalInputRef.current.value.length);
      }
    }
  }, [internalInputRef, props.autoFocus]);
  var typographyVariant = getInputTypographyVariant({
    size: size,
    variant: variant
  });
  var _useCharacterLimit = useCharacterLimit({
      characterLimit: characterLimit,
      endAdornment: endAdornmentProp,
      inputProps: inputPropsProp,
      value: value,
      variant: typographyVariant
    }),
    endAdornment = _useCharacterLimit.endAdornment,
    inputProps = _useCharacterLimit.inputProps;
  var classes = useMemo(function () {
    return getInputClasses({
      appearance: appearance,
      color: color,
      endAdornment: endAdornment,
      shape: shape,
      size: size,
      startAdornment: startAdornment,
      variant: typographyVariant
    });
  }, [appearance, color, endAdornment, shape, size, startAdornment, typographyVariant]);
  var onChange = useCallback(function (event) {
    onChangeProp == null ? void 0 : onChangeProp(event);
    onChangeText == null ? void 0 : onChangeText(event.target.value);
  }, [onChangeProp, onChangeText]);
  return /*#__PURE__*/React__default.createElement(OutlinedInput, _extends({
    ref: ref,
    classes: classes,
    color: "primary",
    endAdornment: endAdornment,
    inputProps: inputProps,
    inputRef: forkedInputRef,
    label: null,
    notched: false,
    startAdornment: startAdornment,
    value: value,
    onChange: onChange
  }, rest));
});
var Input$1 = /*#__PURE__*/React__default.memo(Input);

export { Input$1 as default };
//# sourceMappingURL=index.js.map
